import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  LOCALE_ID,
} from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_NATIVE_DATE_FORMATS,
  NativeDateAdapter,
} from '@angular/material/core';
import { Platform } from '@angular/cdk/platform';

import { routes } from './app.routes';
import { provideAuth } from '@domains/auth';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { cmsMockFeaturesInterceptor } from './core/interceptors/cms-mock-features.interceptor';
import { checkoutCsrfInterceptor } from './core/interceptors/checkout-csrf.interceptor';
import { provideCmsSdk } from '@core/cms-sdk';
import { provideStorefrontFeatures } from '@core/services/storefront-features.provider';
import { provideStorefrontLegal } from '@domains/store/components/legal/legal-config';
import { provideRegretGateway } from '@domains/store/components/arrepentimiento/regret-request.gateway';
import { provideClientHydration, withIncrementalHydration } from '@angular/platform-browser';

registerLocaleData(localeEsAr, 'es-AR');

/** DateAdapter global que inicia la semana en Domingo. */
class SundayFirstDateAdapter extends NativeDateAdapter {
  /**
   * Día con que arranca la semana en los datepickers de Material.
   * @returns `0` (Domingo).
   */
  override getFirstDayOfWeek(): number {
    return 0; // Domingo
  }
}

/**
 * Configuración raíz de la aplicación: router, HTTP + interceptor de auth,
 * proveedores de auth, SDK del CMS, animaciones, locale es-AR e hydration
 * (S7 Fase B). `provideClientHydration` trae el HTTP transfer cache por
 * defecto: las respuestas de Delivery que el server usó para renderizar
 * viajan embebidas en el HTML y el browser las replays post-hydration SIN
 * pegarle a la red — por eso el browser nunca necesita la Delivery key.
 */
export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    // `withPreloading(PreloadAllModules)`: en el browser, tras la carga inicial,
    // precarga los chunks lazy (incl. la ficha de producto) en segundo plano, así
    // el primer click PLP→PDP ya navega client-side sin esperar el chunk (#300).
    provideRouter(routes, withComponentInputBinding(), withPreloading(PreloadAllModules)),
    ...provideAuth(),
    // `checkoutCsrfInterceptor`: refleja la cookie `cms_csrf_token` en el header
    // `X-CSRF-Token` en cada mutación al checkout-proxy (double-submit que el
    // proxy exige). Explícito y determinista — el XSRF built-in de Angular no lo
    // cubría de forma confiable (el header no viajaba en prod → 403 CSRF).
    provideHttpClient(
      withFetch(),
      withInterceptors([checkoutCsrfInterceptor, cmsMockFeaturesInterceptor, authInterceptor]),
    ),
    provideCmsSdk(),
    // Foundation feature flags públicos (runtime per-org): resuelve
    // `mod.cms.storefront.*` en un app-initializer ANTES del primer componente,
    // así el token `FEATURED_OFFERS_ENABLED` inyecta el valor per-org (el CEO lo
    // togglea sin redeploy) con fallback a `environment.*` + telemetría.
    provideStorefrontFeatures(),
    // Centro legal del storefront (#472/#484): único origen de la identidad legal,
    // footer, textos y arrepentimiento. Hoy placeholder; #483 lo swapea 1:1 desde
    // el endpoint de branding per-org sin tocar componentes.
    provideStorefrontLegal(),
    provideRegretGateway(),
    provideAnimations(),
    { provide: LOCALE_ID, useValue: 'es-AR' },
    { provide: MAT_DATE_LOCALE, useValue: 'es-AR' },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    {
      provide: DateAdapter,
      useClass: SundayFirstDateAdapter,
      deps: [MAT_DATE_LOCALE, Platform],
    },
    // Incremental hydration (Angular 21): habilita `@defer (hydrate on ...)`.
    // Incluye event replay, así que reemplaza a `withEventReplay()`. La portada
    // hidrata el carrusel al entrar al viewport (JS diferido, SEO intacto).
    provideClientHydration(withIncrementalHydration()),
  ],
};
